/**
 * A **Call Recording** drawn as bars.
 *
 * The bars are the loudness of the call over its own length, so the
 * shape is the conversation: speech, the gaps between turns, and the
 * silence at the start before anybody has said anything. That is what
 * makes it worth drawing rather than a plain track — somebody who opened
 * a call to hear one part of it can see where the parts are.
 *
 * Read from the same bytes the player is handed, so nothing is fetched
 * twice, and the whole thing degrades to a flat row where a browser will
 * not decode audio it is nonetheless playing.
 */

/**
 * How many bars a recording is drawn as, at any width.
 *
 * The row is scaled to the space it has rather than measured, so this is
 * a resolution and not a count of pixels: enough to show the turns of a
 * short call, few enough that each bar is still a bar on a phone.
 */
export const BAR_COUNT = 160;

/**
 * The loudness of each slice of a recording, 0 to 1 against its own
 * loudest moment.
 *
 * Against its own, because a phone call is quiet: a recording drawn
 * against what the format could have held is a flat line. The root of
 * that ratio rather than the ratio itself, because speech sits well
 * below the two or three loud moments in any call, and the plain ratio
 * draws the conversation as nothing between spikes.
 */
export function barsFrom(samples: Float32Array, count: number): number[] {
  if (count <= 0) return [];
  const per = samples.length / count;
  // Fewer samples than bars is not a recording anybody can draw.
  if (per < 1) return new Array(count).fill(0);

  const bars: number[] = [];
  for (let i = 0; i < count; i++) {
    const start = Math.floor(i * per);
    const end = Math.min(samples.length, Math.floor((i + 1) * per));
    let power = 0;
    for (let j = start; j < end; j++) power += samples[j] * samples[j];
    bars.push(Math.sqrt(power / Math.max(1, end - start)));
  }

  const loudest = Math.max(...bars);
  if (loudest <= 0) return bars.map(() => 0);
  return bars.map((bar) => Math.sqrt(bar / loudest));
}

/** A recording's bars and its length, read off one decode. */
export type Decoded = { bars: number[]; seconds: number };

/**
 * The bars for one recording and how long it runs, or nothing where
 * this browser will not decode it.
 *
 * The length comes off the same decode as the bars, because the player
 * is drawn before its `<audio>` has read the file's header, and a clock
 * reading 0:00 / 0:00 until it does is a player that fills itself in
 * after the page has drawn. Nothing is an ordinary answer, not a
 * failure: it is what a codec the browser plays but will not hand over
 * as samples returns, and the caller draws the flat row instead. The
 * recording still plays and still scrubs, which is what somebody opened
 * it for.
 */
export async function peaksOf(
  audio: Blob,
  count: number = BAR_COUNT,
): Promise<Decoded | null> {
  if (typeof AudioContext === "undefined") return null;
  let ctx: AudioContext | null = null;
  try {
    ctx = new AudioContext();
    const decoded = await ctx.decodeAudioData(await audio.arrayBuffer());
    return {
      bars: barsFrom(decoded.getChannelData(0), count),
      seconds: Number.isFinite(decoded.duration) ? decoded.duration : 0,
    };
  } catch {
    return null;
  } finally {
    void ctx?.close();
  }
}
